import { Module, VuexModule, Mutation, Action } from "vuex-module-decorators";
import Store from "../../index";
import * as modals from "@/lib/modals";
import { BarcodeOrderStatusResource } from "@/models/api/barcodeSystem/barcodeOrderStatus";
import { BarcodeOrderItemStatusResource } from "@/models/api/barcodeSystem/barcodeOrderItemStatus";
import { StockOrderStatusResource } from "@/models/api/barcodeSystem/stockOrderStatus";
import { StockOrderItemStatusResource } from "@/models/api/barcodeSystem/stockOrderItemStatus";
import { LoadStatusResource } from "@/models/api/barcodeSystem/loadStatus";
import { InventoryItemStatusResource } from "@/models/api/barcodeSystem/inventoryItemStatus";
import { DamageCodeResource } from "@/models/api/barcodeSystem/damageCode";
import { ReturnReasonCodeResource } from "@/models/api/barcodeSystem/returnReasonCode";
import { api } from "@/lib";
import { WarehouseFacilityResource } from "../../../models/api/barcodeSystem/inventory/warehouseFacilities";
import { WarehouseLocationCategoryResource } from "@/models/api/barcodeSystem/warehouseLocationCategory";
import { WarehouseLocationResource } from "@/models/api/barcodeSystem/warehouseLocations/get";

const name = "barcodeSystem";

if (Store.state[name]) {
  Store.unregisterModule(name);
}

@Module({ name: name, dynamic: true, store: Store, namespaced: true })
export default class BarcodeSystemModule extends VuexModule {
    public currentFacilityCode: string | null = null;
    public barcodeOrderStatuses: BarcodeOrderStatusResource[] | null = null;
    public barcodeOrderItemStatuses: BarcodeOrderItemStatusResource[] | null = null;
    public stockOrderStatuses: StockOrderStatusResource[] | null = null;
    public stockOrderItemStatuses: StockOrderItemStatusResource[] | null = null;
    public loadStatuses: LoadStatusResource[] | null = null;
    public inventoryItemStatuses: InventoryItemStatusResource[] | null = null;
    public damageCodes: DamageCodeResource[] | null = null;
    public returnReasonCodes: ReturnReasonCodeResource[] | null = null;
    public warehouseFacilities: WarehouseFacilityResource[] | null = null;
    public warehouseLocationCategories: WarehouseLocationCategoryResource[] | null = null;
    

  @Action
  async getBarcodeOrderStatuses() {
    return await api.getAsync("/barcode/orderStatuses");
  }

  @Action
  async loadBarcodeOrderStatuses(): Promise<void> {
    const response = await this.getBarcodeOrderStatuses();
    this.setBarcodeOrderStatuses(response.data);
  }  
  
  @Action
  async getBarcodeOrderItemStatuses() {
    return await api.getAsync("/barcode/orderItemStatuses");
  }

  @Action
  async loadBarcodeOrderItemStatuses(): Promise<void> {
    const response = await this.getBarcodeOrderItemStatuses();
    this.setBarcodeOrderItemStatuses(response.data);
  }

  @Action
  async getStockOrderStatuses() {
    return await api.getAsync("/barcode/stockOrderStatuses");
  }

  @Action
  async loadStockOrderStatuses(): Promise<void> {
    const response = await this.getStockOrderStatuses();
    this.setStockOrderStatuses(response.data);
  }  
  
  @Action
  async getStockOrderItemStatuses() {
    return await api.getAsync("/barcode/stockOrderItemStatuses");
  }

  @Action
  async loadStockOrderItemStatuses(): Promise<void> {
    const response = await this.getStockOrderItemStatuses();
    this.setStockOrderItemStatuses(response.data);
  }
  
  @Action
  async getLoadStatuses() {
    return await api.getAsync("/barcode/loadStatuses");
  }

  @Action
  async loadLoadStatuses(): Promise<void> {
    const response = await this.getLoadStatuses();
    this.setLoadStatuses(response.data);
  }  
  
  @Action
  async getInventoryItemStatuses() {
    return await api.getAsync("/barcode/inventoryItemStatuses");
  }

  @Action
  async loadInventoryItemStatuses(): Promise<void> {
    const response = await this.getInventoryItemStatuses();
    this.setInventoryItemStatuses(response.data);
  }
  
  @Action
  async getDamageCodes() {
    return await api.getAsync("/barcode/damageCodes");
  }

  @Action
  async getDamageCode(code) {
    const response = await api.getAsync(`/barcode/damageCodes/${code}`);
    return response.data;
  }

  @Action
  async loadDamageCodes(): Promise<void> {
    const response = await this.getDamageCodes();
    this.setDamageCodes(response.data);
  } 
  
  @Action
  async getReturnReasonCodes() {
    return await api.getAsync("/barcode/returnreasons");
  }

  @Action
  async getReturnReasonCode(code) {
    const response = await api.getAsync(`/barcode/returnreasons/${code}`);
    return response.data;
  }

  @Action
  async loadReturnReasonCodes(): Promise<void> {
    const response = await this.getReturnReasonCodes();
    this.setReturnReasonCodes(response.data);
  }  
  
  @Action
  async getWarehouseFacilities() {
    return await api.getAsync("/barcode/inventory/warehouseFacilities");
  }

  @Action
  async loadWarehouseFacilities(): Promise<void> {
    const response = await this.getWarehouseFacilities();
    this.setWarehouseFacilties(response.data);
  } 

  @Action
  async getWarehouseLocationCategories() {
    return await api.getAsync("/barcode/warehouseLocationCategories");
  }

  @Action
  async getWarehouseLocationCategory(locationCategory) {
    const response = await api.getAsync(`/barcode/warehouseLocationCategories/${locationCategory}`);
    return response.data;
  }

  @Action
  async loadWarehouseLocationCategories(): Promise<void> {
    const response = await this.getWarehouseLocationCategories();
    this.setWarehouseLocationCategories(response.data);
  } 

  @Action
  barcodeSystemCurrentFacilityShortcutAction() {
    return this.context.dispatch(
      "system/showModal",
      { name: modals.barcodeSystemCurrentFacilityModal },
      { root: true }
    );
  }

  @Action
  currentFacilityIsWarehousing() : boolean
  {
    if (this.currentFacilityCode && this.warehouseFacilities && this.warehouseFacilities.some(x => x.facilityCode == this.currentFacilityCode))
      return true;
    return false;
  }  
  
  @Action
  async createDamageCode(payload) {
    return await api.postAsync("/barcode/damageCodes", payload);
  }  
  
  @Action
  async updateDamageCode(payload) {
    return await api.putAsync(`/barcode/damageCodes/${payload.code}`, payload);
  }

  @Action
  async createReturnReason(payload) {
    return await api.postAsync("/barcode/returnReasons", payload);
  }  
  
  @Action
  async updateReturnReason(payload) {
    return await api.putAsync(`/barcode/returnReasons/${payload.code}`, payload);
  }

  @Mutation
  setBarcodeOrderStatuses(statuses: BarcodeOrderStatusResource[]) {
    this.barcodeOrderStatuses = statuses;
  }

  @Mutation
  setBarcodeOrderItemStatuses(statuses: BarcodeOrderItemStatusResource[]) {
    this.barcodeOrderItemStatuses = statuses;
  }

  @Mutation
  setStockOrderStatuses(statuses: StockOrderStatusResource[]) {
    this.stockOrderStatuses = statuses;
  }

  @Mutation
  setStockOrderItemStatuses(statuses: StockOrderItemStatusResource[]) {
    this.stockOrderItemStatuses = statuses;
  }

  @Mutation
  setLoadStatuses(statuses: LoadStatusResource[]) {
    this.loadStatuses = statuses;
  }

  @Mutation
  setInventoryItemStatuses(statuses: InventoryItemStatusResource[]) {
    this.inventoryItemStatuses = statuses;
  }

  @Mutation
  setDamageCodes(damageCodes: DamageCodeResource[]) {
    this.damageCodes = damageCodes;
  }

  @Mutation
  setReturnReasonCodes(returnReasonCodes: ReturnReasonCodeResource[]) {
    this.returnReasonCodes = returnReasonCodes;
  }  
  
  @Mutation
  setWarehouseFacilties(warehouseFacilities: WarehouseFacilityResource[]) {
    this.warehouseFacilities = warehouseFacilities;
  }
    
  @Mutation
  setWarehouseLocationCategories(warehouseLocationCategories: WarehouseLocationCategoryResource[]) {
    this.warehouseLocationCategories = warehouseLocationCategories;
  }

  @Mutation
  setCurrentFacilityCode(facilityCode: string) {
      this.currentFacilityCode = facilityCode;
  }
}

